import { useRef, useState, type PointerEvent } from 'react';
import { refreshOutline, thumbsDown, thumbsUp } from 'ionicons/icons';
import IonIcon from '../components/IonIcon';
import { isStepNo, isStepResolved, isStepYes } from '../runEvidence';
import type { WebRunStep } from '../runTypes';

interface Props {
  step: WebRunStep;
  pending?: boolean;
  canSwipe: boolean;
  onOpen?: (step: WebRunStep) => void;
  onYes?: (step: WebRunStep) => void;
  onNo?: (step: WebRunStep) => void;
  onReopen?: (step: WebRunStep) => void;
}

const COMMIT = 0.28;
const MAX_TRAVEL = 0.42;

function StatusCircle({ step, pending }: { step: WebRunStep; pending?: boolean }) {
  const yes = isStepYes(step);
  const no = isStepNo(step);
  const tone = no
    ? 'bg-x9-error text-white'
    : yes
      ? 'bg-x9-cyan text-x9-marine'
      : 'border border-x9-mute bg-white text-x9-marineSoft';

  return (
    <span
      className={`inline-flex size-8 shrink-0 items-center justify-center rounded-full ${tone}`}
      aria-label={no ? 'No' : yes ? 'Yes' : 'Open'}
    >
      {pending ? (
        <span className="text-xs font-semibold">…</span>
      ) : no ? (
        <IonIcon icon={thumbsDown} size={15} filled />
      ) : yes ? (
        <IonIcon icon={thumbsUp} size={15} filled />
      ) : null}
    </span>
  );
}

export default function RunStepRow({
  step,
  pending,
  canSwipe,
  onOpen,
  onYes,
  onNo,
  onReopen,
}: Props) {
  const [dx, setDx] = useState(0);
  const start = useRef<{ x: number; y: number } | null>(null);
  const mode = useRef<'idle' | 'maybe' | 'swipe' | 'scroll'>('idle');
  const width = useRef(280);
  const swiped = useRef(false);
  const rowRef = useRef<HTMLDivElement>(null);

  const resolved = isStepResolved(step);
  const swipeOn = canSwipe && !pending;

  const reset = () => {
    start.current = null;
    mode.current = 'idle';
    setDx(0);
  };

  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    if (!swipeOn || e.button !== 0) return;
    width.current = rowRef.current?.offsetWidth || 280;
    start.current = { x: e.clientX, y: e.clientY };
    mode.current = 'maybe';
    swiped.current = false;
  };

  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    if (!start.current || mode.current === 'scroll' || mode.current === 'idle') return;
    const moveX = e.clientX - start.current.x;
    const moveY = e.clientY - start.current.y;
    if (mode.current === 'maybe') {
      if (Math.abs(moveX) < 12 && Math.abs(moveY) < 12) return;
      if (Math.abs(moveY) >= Math.abs(moveX)) {
        mode.current = 'scroll';
        return;
      }
      mode.current = 'swipe';
      e.currentTarget.setPointerCapture(e.pointerId);
    }
    if (mode.current !== 'swipe') return;
    e.preventDefault();
    const cap = width.current * MAX_TRAVEL;
    const next = resolved ? Math.min(0, moveX) : moveX;
    setDx(Math.max(-cap, Math.min(cap, next)));
  };

  const onPointerUp = () => {
    if (mode.current === 'swipe') {
      const threshold = width.current * COMMIT;
      if (dx >= threshold && !resolved) {
        swiped.current = true;
        onYes?.(step);
      } else if (dx <= -threshold) {
        swiped.current = true;
        if (resolved) onReopen?.(step);
        else onNo?.(step);
      }
    }
    reset();
  };

  const handleOpen = () => {
    if (swiped.current || pending) return;
    onOpen?.(step);
  };

  const yesReveal = Math.min(Math.max(dx / (width.current * COMMIT), 0), 1);
  const noReveal = Math.min(Math.max(-dx / (width.current * COMMIT), 0), 1);

  return (
    <div className="relative overflow-hidden">
      <div
        className="absolute inset-0 flex items-center bg-x9-cyan px-5"
        style={{ opacity: yesReveal }}
        aria-hidden="true"
      >
        <IonIcon icon={thumbsUp} size={22} filled className="text-white" />
      </div>
      <div
        className={`absolute inset-0 flex items-center justify-end px-5 ${
          resolved ? 'bg-x9-marine' : 'bg-x9-error'
        }`}
        style={{ opacity: noReveal }}
        aria-hidden="true"
      >
        <IonIcon
          icon={resolved ? refreshOutline : thumbsDown}
          size={22}
          filled={!resolved}
          className="text-white"
        />
      </div>
      <div
        ref={rowRef}
        role="button"
        tabIndex={0}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={reset}
        onClick={handleOpen}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleOpen();
          }
        }}
        className="relative z-[1] flex w-full items-start gap-3 bg-white px-4 py-3 text-left touch-pan-y"
        style={{
          transform: `translateX(${dx}px)`,
          transition: mode.current === 'swipe' ? 'none' : 'transform 180ms ease-out',
        }}
      >
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-medium text-x9-ink">{step.title}</span>
          {step.description ? (
            <span className="mt-0.5 block text-xs leading-relaxed text-x9-marineSoft">
              {step.description}
            </span>
          ) : null}
          {pending ? (
            <span className="mt-1 block text-xs text-x9-marineSoft">Sending…</span>
          ) : null}
        </span>
        <StatusCircle step={step} pending={pending} />
      </div>
    </div>
  );
}
